import type { Webinar } from "@xop/dashboard-shared";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { webinarsApi } from "./client.js";

/**
 * "New webinar" — deliberately the smallest form that can exist.
 *
 * A name is the only required field. Everything else (date, length, join
 * link, description) can be filled in later from step 1, and asking for all
 * of it up front is what makes people abandon a wizard on its first screen.
 */

/** `datetime-local` gives a zone-less "2026-09-01T15:00" — the operator means
 * that wall-clock time, and everything downstream (the list, the invite
 * email) renders UTC explicitly, so treat it as UTC rather than silently
 * shifting it by the browser's offset. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const iso = `${value.length === 16 ? `${value}:00` : value}Z`;
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

const DURATIONS = [30, 45, 60, 90, 120];

export function NewWebinarDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (webinar: Webinar) => void;
}): JSX.Element {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState(60);
  const [link, setLink] = useState("");

  const create = useMutation({
    mutationFn: () =>
      webinarsApi.create({
        title: title.trim(),
        description: description.trim(),
        scheduled_at: localInputToIso(when),
        duration_minutes: duration,
        registration_link: link.trim(),
      }),
    onSuccess: (webinar) => {
      setTitle("");
      setDescription("");
      setWhen("");
      setLink("");
      onClose();
      onCreated(webinar);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New webinar</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {create.isError && (
            <Alert severity="error">
              {(create.error as Error)?.message ?? "Could not create the webinar."}
            </Alert>
          )}
          <TextField
            label="What is it called?"
            placeholder="Keeping 911 online when the network is not"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
            inputProps={{ "data-testid": "webinar-title" }}
          />
          <TextField
            label="What is it about?"
            placeholder="One or two sentences. This is used in the invitation and by the AI later."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="When is it? (UTC)"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="You can leave this blank and set it later."
          />
          <TextField
            label="How long?"
            select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            fullWidth
          >
            {DURATIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {m} minutes
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Where do people join?"
            placeholder="https://..."
            value={link}
            onChange={(e) => setLink(e.target.value)}
            fullWidth
            helperText="The Zoom / Teams / meeting link. Goes in the invitation."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => create.mutate()}
          disabled={!title.trim() || create.isPending}
          data-testid="create-webinar"
        >
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
